const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

export type DateParts = { y: number; m: number; d: number }

/** Parse a calendar date. Never coerces an invalid or missing value to today. */
export function parseIsoDate(iso?: string | null): DateParts | null {
  if (!iso) return null
  const match = ISO_DATE.exec(iso.trim())
  if (!match) return null
  const y = Number(match[1])
  const m = Number(match[2])
  const d = Number(match[3])
  if (m < 1 || m > 12 || d < 1 || d > 31) return null
  const utc = new Date(Date.UTC(y, m - 1, d))
  if (utc.getUTCFullYear() !== y || utc.getUTCMonth() !== m - 1 || utc.getUTCDate() !== d) return null
  return { y, m, d }
}

export function isIsoDate(iso?: string | null): boolean {
  return parseIsoDate(iso) !== null
}

export function formatIsoDate(iso?: string | null): string {
  const parts = parseIsoDate(iso)
  if (!parts) return 'No confirmed date identified'
  return `${parts.d} ${MONTHS[parts.m - 1]} ${parts.y}`
}

export function formatDeadlineDate(iso?: string | null): string {
  if (!iso) return 'No confirmed deadline identified'
  const parts = parseIsoDate(iso)
  if (!parts) return 'No confirmed deadline identified'
  return formatIsoDate(iso)
}

/** Calendar date in Asia/Dubai. Date-only comparisons do not depend on the viewer's offset. */
export function todayIsoInUae(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Dubai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const y = parts.find((p) => p.type === 'year')?.value
  const m = parts.find((p) => p.type === 'month')?.value
  const d = parts.find((p) => p.type === 'day')?.value
  if (!y || !m || !d) return ''
  return `${y}-${m}-${d}`
}

export function compareIso(a?: string | null, b?: string | null): number {
  const aa = parseIsoDate(a) ? (a as string) : ''
  const bb = parseIsoDate(b) ? (b as string) : ''
  if (!aa && !bb) return 0
  if (!aa) return 1
  if (!bb) return -1
  if (aa < bb) return -1
  if (aa > bb) return 1
  return 0
}

export function addDaysIso(iso: string, days: number): string | null {
  const parts = parseIsoDate(iso)
  if (!parts) return null
  const utc = new Date(Date.UTC(parts.y, parts.m - 1, parts.d + days))
  const y = utc.getUTCFullYear()
  const m = String(utc.getUTCMonth() + 1).padStart(2, '0')
  const d = String(utc.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function yearOf(iso?: string | null): number | null {
  return parseIsoDate(iso)?.y ?? null
}

export function inDateRange(iso: string | undefined, start?: string, end?: string): boolean {
  if (!parseIsoDate(iso)) return false
  if (start && compareIso(iso, start) < 0) return false
  if (end && compareIso(iso, end) > 0) return false
  return true
}
